import { apiRequest } from '../lib/api-client';

/** The dashboard and its arrangement (context.md sections 34, 133). */

export type Period = 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'THIS_MONTH' | 'THIS_YEAR';

export type CardSize = 'S' | 'M' | 'L';

export interface PlacedCard {
  card: string;
  size: CardSize;
}

export interface CardResult extends PlacedCard {
  shape: 'FIGURE' | 'BREAKDOWN' | 'SERIES' | 'LIST';
  figure?: {
    value: number;
    secondary?: number;
    /** A percentage answering the same question as the value — the win rate beside what was won. */
    ratio?: number;
    unit?: 'COUNT' | 'CENTS' | 'PERCENT';
    previous?: number;
    delta?: number | null;
  };
  points?: { day: string; value: number }[];
  slices?: { label: string; value: number; color?: string }[];
  unit?: 'COUNT' | 'CENTS' | 'PERCENT';
  rows?: {
    id: string;
    title: string;
    detail: string | null;
    badge: string | null;
    href?: string;
    tone?: string;
    /** The numbers behind the row. The wording is this side's job (context.md §130). */
    data?: Record<string, number | string | null>;
  }[];
}

export interface DashboardView {
  period: Period;
  from: string;
  to: string;
  scope: 'TENANT' | 'PERSONAL';
  cards: CardResult[];
}

export function getDashboard(period: Period): Promise<DashboardView> {
  return apiRequest(`/v1/dashboard?period=${period}`);
}

export interface CatalogueEntry {
  key: string;
  group: string;
  shape: string;
  sizes: CardSize[];
  periodic: boolean;
  available: boolean;
}

export function getCardCatalogue(): Promise<{ cards: CatalogueEntry[] }> {
  return apiRequest('/v1/dashboard/cards');
}

export interface LayoutView {
  scope: 'TENANT' | 'PERSONAL';
  cards: PlacedCard[];
  filters: Record<string, unknown>;
}

export function getLayout(): Promise<LayoutView> {
  return apiRequest('/v1/dashboard/layout');
}

export function saveLayout(input: {
  scope: 'TENANT' | 'PERSONAL';
  cards: PlacedCard[];
}): Promise<LayoutView> {
  return apiRequest('/v1/dashboard/layout', { method: 'PUT', body: input });
}

/**
 * Drops an arrangement.
 *
 * `PERSONAL` puts this person back on the company's; `TENANT` puts the whole company back on the
 * product's default, which is the only way to reach a later release's screen once somebody has
 * saved an arrangement for everyone.
 */
export function resetLayout(scope: 'TENANT' | 'PERSONAL' = 'PERSONAL'): Promise<LayoutView> {
  return apiRequest(`/v1/dashboard/layout?scope=${scope}`, { method: 'DELETE' });
}
